import React, { useState, useEffect, useCallback, useRef } from 'react';
import { taskAPI, clientAPI, serviceAPI, taskTypeAPI, userAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import {
  Plus,
  CheckCircle,
  Circle,
  Calendar,
  User,
  Users,
  DollarSign,
  Receipt,
  Edit2,
  AlertCircle,
  Search,
  Filter,
  Play,
  Square,
  Clock,
  StopCircle,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  X,
  Ban,
  Banknote,
  Eye,
  Printer,
  MessageSquare,
  Send,
} from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tasks = () => {
  const { user, isPartner, isManager } = useAuth();
  const location = useLocation();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Active work tracking
  const [activeWork, setActiveWork] = useState(null);
  const [runningTime, setRunningTime] = useState(0);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterUser, setFilterUser] = useState('');
  
  // Create Task Form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [taskForm, setTaskForm] = useState({
    client_id: '',
    service_id: '',
    task_type_id: '',
    assigned_to_ids: [],  // Multiple assignees
    due_date: '',
    priority: 'Medium',
    notes: '',
  });
  
  // Client search
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Invoice Form
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedTaskForInvoice, setSelectedTaskForInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ number: '', date: '', amount: '', description: '' });

  // Receipt Form
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedTaskForReceipt, setSelectedTaskForReceipt] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ number: '', date: '', amount: '', mode: 'Bank' });

  // Edit Status/Priority
  const [editingTask, setEditingTask] = useState(null);
  
  // Add Subtask
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false);
  const [selectedTaskForSubtask, setSelectedTaskForSubtask] = useState(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  // Client Info Modal
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);
  const [selectedClientInfo, setSelectedClientInfo] = useState(null);
  const [loadingClientInfo, setLoadingClientInfo] = useState(false);

  // Reimbursement Modal
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [selectedTaskForReimbursement, setSelectedTaskForReimbursement] = useState(null);
  const [reimbursementForm, setReimbursementForm] = useState({ 
    type: 'Govt Fee', 
    description: '', 
    amount: '', 
    date: new Date().toISOString().split('T')[0],
    status: 'Pending'
  });

  // Non-billable Modal
  const [showNonBillableModal, setShowNonBillableModal] = useState(false);
  const [selectedTaskForNonBillable, setSelectedTaskForNonBillable] = useState(null);
  const [nonBillableReason, setNonBillableReason] = useState('');

  // View Receipt Modal
  const [showViewReceiptModal, setShowViewReceiptModal] = useState(false);
  const [selectedTaskForViewReceipt, setSelectedTaskForViewReceipt] = useState(null);

  const [notifyDropdownTask, setNotifyDropdownTask] = useState(null);
  const [statutoryDueDates, setStatutoryDueDates] = useState([]);

  // Fetch active work
  const fetchActiveWork = useCallback(async () => {
    try {
      const res = await taskAPI.getActiveWork();
      setActiveWork(res.data.active_task);
      if (res.data.active_task) {
        setRunningTime(res.data.active_task.running_minutes || 0);
      }
    } catch (err) {
      console.error('Error fetching active work:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchActiveWork();
  }, []);

  // Timer for active work
  useEffect(() => {
    let interval;
    if (activeWork) {
      interval = setInterval(() => {
        setRunningTime((prev) => prev + 1);
      }, 60000); // Update every minute
    }
    return () => clearInterval(interval);
  }, [activeWork]);

  useEffect(() => {
    // Apply filter from URL query params
    const params = new URLSearchParams(location.search);
    const filter = params.get('filter');
    
    if (filter === 'overdue') {
      setFilterStatus('Overdue');
    } else if (filter === 'today') {
      // Will filter in the filteredTasks logic
      setFilterStatus('today');
    } else if (filter === 'week') {
      setFilterStatus('week');
    }
  }, [location.search]);

  // Close notify dropdown on outside click
  useEffect(() => {
    if (!notifyDropdownTask) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-notify-dropdown]')) {
        setNotifyDropdownTask(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifyDropdownTask]);

  const handleStartWork = async (taskId) => {
    try {
      await taskAPI.startWork(taskId);
      await fetchActiveWork();
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start work');
    }
  };

  const handleStopWork = async (taskId) => {
    try {
      await taskAPI.stopWork(taskId);
      setActiveWork(null);
      setRunningTime(0);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to stop work');
    }
  };

  const handleForceStopWork = async (taskId, userName) => {
    if (!window.confirm(`Force stop ${userName}'s work session on this task?`)) {
      return;
    }
    try {
      const res = await taskAPI.forceStopWork(taskId);
      alert(res.data.message);
      setActiveWork(null);
      setRunningTime(0);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to force stop work');
    }
  };

  // Format time in hours and minutes
  const formatTime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const fetchData = async () => {
    try {
      // Fetch only active tasks (not completed/invoiced)
      const [tasksRes, clientsRes, servicesRes, taskTypesRes, usersRes] = await Promise.all([
        taskAPI.getAll({ status: 'active' }),
        clientAPI.getAll(),
        serviceAPI.getAll(),
        taskTypeAPI.getAll(),
        (isPartner || isManager) ? userAPI.getAll() : Promise.resolve({ data: [] }),
      ]);

      setTasks(tasksRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
      setTaskTypes(taskTypesRes.data);
      setUsers(usersRes.data);

      // Fetch upcoming statutory due dates
      try {
        const token = localStorage.getItem('token');
        const statutoryRes = await fetch(`${API_URL}/api/statutory-calendar/upcoming`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statutoryRes.ok) {
          const data = await statutoryRes.json();
          setStatutoryDueDates(data.filter(d => d.days_left >= -3 && d.days_left <= 15));
        }
      } catch (e) {
        console.error('Error fetching statutory dates:', e);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter((client) => {
    if (!clientSearchTerm) return true;
    const search = clientSearchTerm.toLowerCase();
    return (
      client.name?.toLowerCase().includes(search) ||
      client.client_code?.toLowerCase().includes(search) ||
      client.gst_number?.toLowerCase().includes(search) ||
      client.pan?.toLowerCase().includes(search) ||
      client.phone?.includes(search)
    );
  });

  const handleClientSelect = (client) => {
    setTaskForm({ ...taskForm, client_id: client.id });
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.client_id || !taskForm.service_id || !taskForm.task_type_id || !taskForm.assigned_to_ids.length || !taskForm.due_date) {
      alert('Please fill in all required fields (including at least one assignee)');
      return;
    }

    setLoading(true);
    try {
      await taskAPI.create(taskForm);
      setTaskForm({
        client_id: '',
        service_id: '',
        task_type_id: '',
        assigned_to_ids: [],
        due_date: '',
        priority: 'Medium',
        notes: '',
      });
      setClientSearchTerm('');
      setShowCreateForm(false);
      fetchData();
    } catch (error) {
      alert('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSubtask = async (taskId, subtaskId) => {
    try {
      await taskAPI.toggleSubtask(taskId, subtaskId);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle subtask:', error);
    }
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceForm.number || !invoiceForm.date || !invoiceForm.amount) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const result = await taskAPI.addInvoice(selectedTaskForInvoice, {
        ...invoiceForm,
        amount: parseFloat(invoiceForm.amount),
      });
      setShowInvoiceModal(false);
      setInvoiceForm({ number: '', date: '', amount: '', description: '' });
      fetchData();
      
      // Show payment link if generated
      const paymentLink = result?.data?.payment_link;
      if (paymentLink) {
        const sendNow = window.confirm(
          `Invoice created with payment link!\n\n` +
          `Payment Link: ${paymentLink}\n\n` +
          `Click OK to copy the link and go to Reminders to send it to the client.`
        );
        if (sendNow) {
          navigator.clipboard?.writeText(paymentLink).catch(() => {});
        }
      }
    } catch (error) {
      alert('Failed to add invoice');
    }
  };

  const handleAddReceipt = async (e) => {
    e.preventDefault();
    if (!receiptForm.number || !receiptForm.date || !receiptForm.amount || !receiptForm.mode) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await taskAPI.addReceipt(selectedTaskForReceipt, {
        ...receiptForm,
        amount: parseFloat(receiptForm.amount),
      });
      setShowReceiptModal(false);
      setReceiptForm({ number: '', date: '', amount: '', mode: 'Bank' });
      fetchData();
    } catch (error) {
      alert('Failed to add receipt');
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    // Update local state immediately for responsive UI
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { ...task, status: newStatus } : task
      )
    );
    
    try {
      await taskAPI.updateStatus(taskId, newStatus);
    } catch (error) {
      console.error('Status update error:', error.response?.data || error);
      alert('Failed to update status: ' + (error.response?.data?.detail || 'Unknown error'));
      // Revert on error
      fetchData();
    }
  };

  const handleUpdatePriority = async (taskId, newPriority) => {
    // Update local state immediately for responsive UI
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { ...task, priority: newPriority } : task
      )
    );
    
    try {
      await taskAPI.update(taskId, { priority: newPriority });
    } catch (error) {
      console.error('Priority update error:', error.response?.data || error);
      alert('Failed to update priority: ' + (error.response?.data?.detail || 'Unknown error'));
      // Revert on error
      fetchData();
    }
  };

  const handleUpdateDueDate = async (taskId, newDueDate) => {
    // Update local state immediately
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { ...task, due_date: newDueDate } : task
      )
    );
    
    try {
      await taskAPI.update(taskId, { due_date: newDueDate });
    } catch (error) {
      console.error('Due date update error:', error.response?.data || error);
      alert('Failed to update due date: ' + (error.response?.data?.detail || 'Unknown error'));
      fetchData();
    }
  };

  const handleUpdateAssignedTo = async (taskId, newUserIds) => {
    // newUserIds can be a single ID (string) or array of IDs
    const userIdsArray = Array.isArray(newUserIds) ? newUserIds : [newUserIds];
    const newUserNames = userIdsArray.map(id => users.find(u => u.id === id)?.full_name || '');
    
    // Update local state immediately
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId ? { 
          ...task, 
          assigned_to: userIdsArray[0], 
          assigned_to_name: newUserNames[0],
          assigned_to_ids: userIdsArray,
          assigned_to_names: newUserNames
        } : task
      )
    );
    
    try {
      await taskAPI.update(taskId, { assigned_to_ids: userIdsArray });
    } catch (error) {
      console.error('Assigned user update error:', error.response?.data || error);
      alert('Failed to update assigned user: ' + (error.response?.data?.detail || 'Unknown error'));
      fetchData();
    }
  };

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) {
      alert('Please enter subtask title');
      return;
    }

    try {
      // Use dedicated API endpoint for adding subtask
      await taskAPI.addSubtask(selectedTaskForSubtask, newSubtaskTitle.trim());
      
      setShowAddSubtaskModal(false);
      setNewSubtaskTitle('');
      setSelectedTaskForSubtask(null);
      fetchData();
    } catch (error) {
      alert('Failed to add subtask');
    }
  };

  // Fetch client info for popup
  const handleShowClientInfo = async (clientId) => {
    setLoadingClientInfo(true);
    setShowClientInfoModal(true);
    try {
      const response = await clientAPI.getById(clientId);
      setSelectedClientInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch client info:', error);
      setSelectedClientInfo(null);
    } finally {
      setLoadingClientInfo(false);
    }
  };

  // Handle mark as non-billable - opens modal
  const openNonBillableModal = (taskId) => {
    setSelectedTaskForNonBillable(taskId);
    setNonBillableReason('');
    setShowNonBillableModal(true);
  };

  // Handle confirm non-billable
  const handleConfirmNonBillable = async () => {
    if (!nonBillableReason.trim()) {
      alert('Please enter a reason for marking as non-billable');
      return;
    }
    try {
      await taskAPI.markNonBillable(selectedTaskForNonBillable, nonBillableReason.trim());
      setShowNonBillableModal(false);
      setSelectedTaskForNonBillable(null);
      setNonBillableReason('');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark task as non-billable');
    }
  };

  // Handle add reimbursement
  const handleAddReimbursement = async (e) => {
    e.preventDefault();
    if (!reimbursementForm.type || !reimbursementForm.amount || !reimbursementForm.date) {
      alert('Please fill in type, amount and date');
      return;
    }

    try {
      await taskAPI.addReimbursement(selectedTaskForReimbursement, {
        ...reimbursementForm,
        amount: parseFloat(reimbursementForm.amount),
      });
      setShowReimbursementModal(false);
      setReimbursementForm({ type: 'Govt Fee', description: '', amount: '', date: new Date().toISOString().split('T')[0], status: 'Pending' });
      setSelectedTaskForReimbursement(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add reimbursement');
    }
  };

  // Handle collect reimbursement
  const handleCollectReimbursement = async (taskId, reimbursementId) => {
    try {
      await taskAPI.collectReimbursement(taskId, reimbursementId);
      fetchData();
    } catch (error) {
      alert('Failed to mark reimbursement as collected');
    }
  };

  // Request additional info from client
  const handleRequestInfo = async (taskId, customMessage = null) => {
    try {
      const token = localStorage.getItem('token');
      const url = customMessage 
        ? `${API_URL}/api/tasks/${taskId}/request-info?message=${encodeURIComponent(customMessage)}`
        : `${API_URL}/api/tasks/${taskId}/request-info`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Reminder created successfully!\n\nGo to Reminders page to view and send it to the client.`);
      } else {
        const error = await response.json();
        alert('Failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error requesting info:', error);
      alert('Failed to create reminder');
    }
  };

  // Send status update to client
  const handleSendStatusUpdate = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/send-status-update`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('Status update reminder created!\n\nGo to Reminders page to review and send it to the client.');
      } else {
        const error = await response.json();
        alert('Failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending status update:', error);
      alert('Failed to create status update');
    }
  };

  // Number to words conversion for Indian numbering
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    
    const convertBelowThousand = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertBelowThousand(n % 100) : '');
    };
    
    let result = '';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = num % 1000;
    
    if (crore > 0) result += convertBelowThousand(crore) + ' Crore ';
    if (lakh > 0) result += convertBelowThousand(lakh) + ' Lakh ';
    if (thousand > 0) result += convertBelowThousand(thousand) + ' Thousand ';
    if (hundred > 0) result += convertBelowThousand(hundred);
    
    return result.trim() + ' Only';
  };

  // Handle print receipt
  const handlePrintReceipt = (task) => {
    if (!task || !task.receipts || task.receipts.length === 0) return;
    
    const receipts = task.receipts;
    const totalReceived = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
    
    // Generate receipt rows
    let receiptRows = '';
    receipts.forEach((r, idx) => {
      const receiptDate = r.date ? new Date(r.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-') : '-';
      receiptRows += `
        <tr>
          <td>${idx + 1}</td>
          <td>${receiptDate}</td>
          <td>${r.number || '-'}</td>
          <td style="text-align: right;">₹${(r.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
          <td>${r.mode || '-'}</td>
        </tr>
      `;
    });
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${task.client_name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; font-size: 14px; }
            .receipt-container { max-width: 700px; margin: 0 auto; border: 2px solid #000; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 15px; }
            .company-name { font-size: 24px; font-weight: bold; color: #1a365d; margin-bottom: 5px; }
            .company-title { font-size: 14px; font-weight: 500; color: #333; }
            .company-address { font-size: 11px; color: #555; margin-top: 5px; }
            .receipt-title { text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; text-decoration: underline; color: #0d9488; }
            .info-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .info-box { }
            .info-label { font-size: 12px; color: #666; }
            .info-value { font-size: 14px; font-weight: bold; }
            .party-section { margin-bottom: 20px; padding: 10px; background: #f5f5f5; border-radius: 5px; }
            .party-label { font-size: 12px; color: #666; }
            .party-name { font-size: 18px; font-weight: bold; }
            .invoice-ref { font-size: 12px; color: #666; margin-top: 5px; }
            .receipt-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .receipt-table th, .receipt-table td { padding: 10px; text-align: left; border: 1px solid #ddd; }
            .receipt-table th { background: #f0f0f0; font-weight: 600; }
            .total-section { text-align: right; margin: 20px 0; padding: 15px; background: #e8f5e9; border-radius: 5px; }
            .total-label { font-size: 14px; }
            .total-amount { font-size: 24px; font-weight: bold; color: #2e7d32; }
            .amount-words { font-style: italic; font-size: 12px; margin-top: 5px; }
            .thanks-section { text-align: center; margin-top: 30px; padding: 15px; background: #fff3e0; border-radius: 5px; }
            .thanks-text { font-size: 16px; font-weight: 500; color: #e65100; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; }
            .signature-box { text-align: right; }
            .signature-line { border-top: 1px solid #000; width: 200px; margin-left: auto; padding-top: 5px; }
            @media print { 
              body { padding: 10px; } 
              .receipt-container { border: 1px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              <img src="${window.location.origin}/images/ca-logo.png" alt="CA" style="height: 50px; margin-bottom: 8px;" />
              <div class="company-name">HYDER ALI AND ASSOCIATES</div>
              <div class="company-title">CHARTERED ACCOUNTANTS</div>
              <div class="company-address">XI/405,406,407, Sahara Building, Kadappadi, Peruvallur, Kondotty Malappuram, Kerala- 673638</div>
              <div class="company-address">E-Mail: cahyderali@gmail.com</div>
            </div>
            
            <div class="receipt-title">PAYMENT RECEIPT</div>
            
            <div class="info-section">
              <div class="info-box">
                <div class="info-label">Date:</div>
                <div class="info-value">${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
              </div>
              ${task.invoice ? `
              <div class="info-box">
                <div class="info-label">Invoice Reference:</div>
                <div class="info-value">${task.invoice.number || '-'}</div>
              </div>
              ` : ''}
            </div>
            
            <div class="party-section">
              <div class="party-label">Received From:</div>
              <div class="party-name">${task.client_name || '-'}</div>
              <div class="invoice-ref">Service: ${task.service_name} - ${task.task_type_name || ''}</div>
            </div>
            
            <table class="receipt-table">
              <thead>
                <tr>
                  <th style="width: 50px;">SI No.</th>
                  <th>Date</th>
                  <th>Receipt No.</th>
                  <th style="text-align: right;">Amount</th>
                  <th>Mode</th>
                </tr>
              </thead>
              <tbody>
                ${receiptRows}
              </tbody>
            </table>
            
            <div class="total-section">
              <div class="total-label">Total Amount Received:</div>
              <div class="total-amount">₹${totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              <div class="amount-words">INR ${numberToWords(Math.round(totalReceived))}</div>
            </div>
            
            <div class="thanks-section">
              <div class="thanks-text">Thank you for your payment!</div>
            </div>
            
            <div class="footer">
              <div></div>
              <div class="signature-box">
                <div class="signature-line">Authorised Signatory</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Filter tasks based on search and filters
  const filteredTasks = tasks.filter((task) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDueDate = new Date(task.due_date);
    taskDueDate.setHours(0, 0, 0, 0);
    const weekFromNow = new Date(today);
    weekFromNow.setDate(today.getDate() + 7);

    // Search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const assigneeNames = (task.assigned_to_names || [task.assigned_to_name]).join(' ').toLowerCase();
      const matchesSearch =
        task.client_name?.toLowerCase().includes(search) ||
        task.service_name?.toLowerCase().includes(search) ||
        task.task_type_name?.toLowerCase().includes(search) ||
        assigneeNames.includes(search);
      if (!matchesSearch) return false;
    }

    // Client filter
    if (filterClient && task.client_id !== filterClient) return false;

    // Status filter
    if (filterStatus && filterStatus !== 'today' && filterStatus !== 'week') {
      if (task.status !== filterStatus) return false;
    }

    // Due today filter
    if (filterStatus === 'today') {
      if (taskDueDate.getTime() !== today.getTime()) return false;
    }

    // Due this week filter
    if (filterStatus === 'week') {
      if (taskDueDate < today || taskDueDate > weekFromNow) return false;
    }

    // Priority filter
    if (filterPriority && task.priority !== filterPriority) return false;

    // User filter (for partner/manager only) - check both legacy and new fields
    if (filterUser) {
      const assignedIds = task.assigned_to_ids || [task.assigned_to];
      if (!assignedIds.includes(filterUser)) return false;
    }

    return true;
  });

  const getStatusBadge = (status) => {
    const colors = {
      'To Do': 'bg-gray-100 text-gray-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'Done': 'bg-green-100 text-green-700',
      'Overdue': 'bg-red-100 text-red-700',
    };
    return colors[status] || colors['To Do'];
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Low': 'bg-green-100 text-green-700',
      'Medium': 'bg-yellow-100 text-yellow-700',
      'High': 'bg-red-100 text-red-700',
    };
    return colors[priority] || colors['Medium'];
  };

  const canCreateTask = isPartner || isManager;

  return (
    <div className="space-y-6">
      {/* Statutory Due Dates Banner */}
      {statutoryDueDates.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4" data-testid="statutory-banner">
          <h3 className="text-sm font-semibold text-purple-800 mb-2 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Upcoming Statutory Due Dates
          </h3>
          <div className="flex flex-wrap gap-2">
            {statutoryDueDates.map((d, i) => (
              <span key={i} className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                d.is_overdue ? 'bg-red-100 text-red-800' :
                d.is_today ? 'bg-yellow-100 text-yellow-800' :
                d.days_left <= 3 ? 'bg-orange-100 text-orange-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {d.name} — {d.is_overdue ? `Overdue (${d.due_date})` : d.is_today ? 'Today' : `${d.days_left}d left (${new Date(d.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })})`}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Active Tasks</h2>
        {canCreateTask && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            data-testid="create-task-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium mb-1">
              <Search className="w-4 h-4 inline mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by client, service, task type, staff..."
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="search-tasks-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Client</label>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Status</option>
              <option value="To Do">To Do</option>
              <option value="In Progress">In Progress</option>
              <option value="Done">Done</option>
              <option value="Overdue">Overdue</option>
              <option value="today">Due Today</option>
              <option value="week">Due This Week</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Priorities</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>

          {/* User Filter - Only for Partner/Manager */}
          {(isPartner || isManager) && (
            <div>
              <label className="block text-sm font-medium mb-1">Assigned To</label>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="filter-user"
              >
                <option value="">All Users</option>
                {users.filter(u => u.is_active).map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-4">
          <p className="text-sm text-gray-600">
            Showing {filteredTasks.length} of {tasks.length} tasks
          </p>
          {(searchTerm || filterClient || filterStatus || filterPriority || filterUser) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterClient('');
                setFilterStatus('');
                setFilterPriority('');
                setFilterUser('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Create Task Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4">Create New Task</h3>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Client *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={clientSearchTerm}
                    onChange={(e) => {
                      setClientSearchTerm(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="Search client by name, code, GST, PAN..."
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                  {showClientDropdown && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map((client) => (
                        <div
                          key={client.id}
                          onClick={() => handleClientSelect(client)}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="font-medium">{client.name}</div>
                          <div className="text-sm text-gray-500">
                            {client.client_code} 
                            {client.gst_number && ` | GST: ${client.gst_number}`}
                            {client.pan && ` | PAN: ${client.pan}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Service *</label>
                <select
                  value={taskForm.service_id}
                  onChange={(e) => {
                    setTaskForm({ ...taskForm, service_id: e.target.value, task_type_id: '' });
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Task Type *</label>
                <select
                  value={taskForm.task_type_id}
                  onChange={(e) => setTaskForm({ ...taskForm, task_type_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  disabled={!taskForm.service_id}
                >
                  <option value="">Select Task Type</option>
                  {taskTypes
                    .filter(tt => tt.service_id === taskForm.service_id)
                    .map((taskType) => (
                      <option key={taskType.id} value={taskType.id}>{taskType.name}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assign To * (Hold Ctrl/Cmd to select multiple)</label>
                <select
                  multiple
                  value={taskForm.assigned_to_ids}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    setTaskForm({ ...taskForm, assigned_to_ids: selectedOptions });
                  }}
                  className="w-full px-3 py-2 border rounded-lg min-h-[120px]"
                  required
                >
                  {users.filter(user => user.is_active).map((user) => (
                    <option key={user.id} value={user.id}>{user.full_name} ({user.role})</option>
                  ))}
                </select>
                {taskForm.assigned_to_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {taskForm.assigned_to_ids.map(id => users.find(u => u.id === id)?.full_name).filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Due Date *</label>
                <input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Priority</label>
                <select
                  value={taskForm.priority}
                  onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={taskForm.notes}
                onChange={(e) => setTaskForm({ ...taskForm, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                rows="2"
              />
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                {loading ? 'Creating...' : 'Create Task'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-4">
        {filteredTasks.map((task) => (
          <div key={task.id} className={`bg-white p-4 lg:p-6 rounded-lg shadow border ${activeWork?.id === task.id ? 'ring-2 ring-green-500' : ''}`}>
            <div className="flex items-start justify-between mb-3 lg:mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2 flex-wrap">
                  <h3 
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                    onClick={() => handleShowClientInfo(task.client_id)}
                    title="Click to view client details"
                    data-testid="client-name-link"
                  >
                    {task.client_name}
                  </h3>
                  
                  {/* Editable Status */}
                  {canCreateTask ? (
                    <select
                      value={task.status}
                      onChange={(e) => handleUpdateStatus(task.id, e.target.value)}
                      className={`px-2 py-1 text-xs rounded border-0 ${getStatusBadge(task.status)}`}
                    >
                      <option value="To Do">To Do</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Done">Done</option>
                      <option value="Overdue">Overdue</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 text-xs rounded ${getStatusBadge(task.status)}`}>
                      {task.status}
                    </span>
                  )}
                  
                  {/* Editable Priority */}
                  {canCreateTask ? (
                    <select
                      value={task.priority}
                      onChange={(e) => handleUpdatePriority(task.id, e.target.value)}
                      className={`px-2 py-1 text-xs rounded border-0 ${getPriorityBadge(task.priority)}`}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  ) : (
                    <span className={`px-2 py-1 text-xs rounded ${getPriorityBadge(task.priority)}`}>
                      {task.priority}
                    </span>
                  )}
                  
                  {/* Total Time Badge */}
                  {task.total_time_minutes > 0 && (
                    <span className="px-2 py-1 text-xs rounded bg-purple-100 text-purple-700 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatTime(task.total_time_minutes)}
                    </span>
                  )}
                </div>
                
                {/* Notes - Show first if exists */}
                {task.notes && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2">
                    <p className="text-sm text-yellow-800">{task.notes}</p>
                  </div>
                )}
                
                {/* Task Name */}
                {task.task_name && (
                  <p className="text-base font-medium text-gray-800 mb-1">{task.task_name}</p>
                )}
                
                <p className="text-sm text-gray-600">
                  {task.service_name} → {task.task_type_name}
                </p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 flex-wrap">
                  {/* Editable Due Date */}
                  {canCreateTask ? (
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <input
                        type="date"
                        value={task.due_date ? task.due_date.split('T')[0] : ''}
                        onChange={(e) => handleUpdateDueDate(task.id, e.target.value)}
                        className="border-0 bg-transparent text-sm text-gray-600 cursor-pointer hover:text-blue-600"
                      />
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Due: {formatDate(task.due_date)}
                    </span>
                  )}
                  
                  {/* Editable Assigned Users (Multiple) */}
                  {canCreateTask ? (
                    <span className="flex items-center relative group">
                      <Users className="w-4 h-4 mr-1" />
                      <span 
                        className="text-sm text-gray-600 cursor-pointer hover:text-blue-600"
                        onClick={(e) => {
                          // Show multi-select dropdown
                          const dropdown = e.currentTarget.nextElementSibling;
                          if (dropdown) dropdown.classList.toggle('hidden');
                        }}
                      >
                        {(task.assigned_to_names || [task.assigned_to_name]).join(', ')}
                      </span>
                      <div className="hidden absolute top-full left-0 z-20 bg-white border rounded-lg shadow-lg p-2 min-w-[200px]">
                        <p className="text-xs text-gray-500 mb-2">Select assignees:</p>
                        {users.filter(u => u.is_active).map((u) => (
                          <label key={u.id} className="flex items-center p-1 hover:bg-gray-100 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(task.assigned_to_ids || [task.assigned_to]).includes(u.id)}
                              onChange={(e) => {
                                const currentIds = task.assigned_to_ids || [task.assigned_to];
                                let newIds;
                                if (e.target.checked) {
                                  newIds = [...currentIds, u.id];
                                } else {
                                  newIds = currentIds.filter(id => id !== u.id);
                                }
                                if (newIds.length > 0) {
                                  handleUpdateAssignedTo(task.id, newIds);
                                }
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">{u.full_name}</span>
                          </label>
                        ))}
                      </div>
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {(task.assigned_to_names || [task.assigned_to_name]).join(', ')}
                    </span>
                  )}
                  {task.task_code && (
                    <span className="text-xs text-gray-400">{task.task_code}</span>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mt-3 lg:mt-0 lg:flex-col lg:space-y-0 lg:ml-4">
                {/* Start/Stop Work Button */}
                {activeWork?.id === task.id ? (
                  // Current user has active session on this task
                  <button
                    onClick={() => handleStopWork(task.id)}
                    className="bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 flex items-center"
                    data-testid="stop-work-button"
                  >
                    <Square className="w-4 h-4 mr-1" />
                    Stop ({formatTime(runningTime)})
                  </button>
                ) : task.is_working ? (
                  // Someone else has active session on this task
                  <>
                    {/* Show who is working */}
                    <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded flex items-center">
                      <Clock className="w-3 h-3 mr-1 animate-pulse" />
                      {task.time_entries?.find(e => !e.end_time)?.user_name || 'Someone'} is working
                    </div>
                    {/* Force Stop - Partner Only */}
                    {isPartner && (
                      <button
                        onClick={() => handleForceStopWork(task.id, task.time_entries?.find(e => !e.end_time)?.user_name || 'User')}
                        className="bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 flex items-center"
                        data-testid="force-stop-button"
                      >
                        <StopCircle className="w-4 h-4 mr-1" />
                        Force Stop
                      </button>
                    )}
                  </>
                ) : (
                  // No one is working - show Start button
                  <button
                    onClick={() => handleStartWork(task.id)}
                    disabled={activeWork !== null}
                    className={`px-3 py-2 rounded text-sm flex items-center ${
                      activeWork !== null 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    data-testid="start-work-button"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Start Work
                  </button>
                )}
                
                {/* Add Invoice - only for billable tasks without invoice */}
                {canCreateTask && !task.invoice && task.is_billable !== false && (
                  <button
                    onClick={async () => {
                      setSelectedTaskForInvoice(task.id);
                      // Fetch next invoice number
                      try {
                        const response = await taskAPI.getNextInvoiceNumber();
                        const today = new Date().toISOString().split('T')[0];
                        setInvoiceForm({ 
                          number: response.data.next_number, 
                          date: today, 
                          amount: '', 
                          description: task.service_name + ' - ' + (task.task_type_name || '') 
                        });
                      } catch (error) {
                        console.error('Failed to get next invoice number:', error);
                        setInvoiceForm({ number: '', date: '', amount: '', description: '' });
                      }
                      setShowInvoiceModal(true);
                    }}
                    className="bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 flex items-center"
                    data-testid="add-invoice-button"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Add Invoice
                  </button>
                )}

                {/* Mark as Non-billable - Partner/Manager only, for tasks without invoice */}
                {canCreateTask && !task.invoice && task.is_billable !== false && (
                  <button
                    onClick={() => openNonBillableModal(task.id)}
                    className="bg-gray-500 text-white px-3 py-2 rounded text-sm hover:bg-gray-600 flex items-center"
                    data-testid="mark-non-billable-button"
                    title="Mark as non-billable task"
                  >
                    <Ban className="w-4 h-4 mr-1" />
                    Non-billable
                  </button>
                )}

                {/* Non-billable indicator with reason */}
                {task.is_billable === false && (
                  <div className="flex flex-col">
                    <span className="bg-gray-200 text-gray-600 px-3 py-2 rounded text-sm flex items-center">
                      <Ban className="w-4 h-4 mr-1" />
                      Non-billable
                    </span>
                    {task.non_billable_reason && (
                      <span className="text-xs text-gray-500 mt-1" title={task.non_billable_reason}>
                        Reason: {task.non_billable_reason.substring(0, 30)}{task.non_billable_reason.length > 30 ? '...' : ''}
                      </span>
                    )}
                  </div>
                )}

                {/* Add Reimbursement - Partner/Manager only, available for all tasks */}
                {canCreateTask && (
                  <button
                    onClick={() => {
                      setSelectedTaskForReimbursement(task.id);
                      setReimbursementForm({ 
                        type: 'Govt Fee', 
                        description: '', 
                        amount: '', 
                        date: new Date().toISOString().split('T')[0],
                        status: 'Pending'
                      });
                      setShowReimbursementModal(true);
                    }}
                    className="bg-amber-500 text-white px-3 py-2 rounded text-sm hover:bg-amber-600 flex items-center"
                    data-testid="add-reimbursement-button"
                    title="Add Tax/Govt Fee for collection"
                  >
                    <Banknote className="w-4 h-4 mr-1" />
                    Add Reimbursement
                  </button>
                )}

                {/* Notify Client - Combined dropdown for Request Info & Status Update */}
                <div className="relative" data-notify-dropdown>
                  <button
                    onClick={(e) => { e.stopPropagation(); setNotifyDropdownTask(notifyDropdownTask === task.id ? null : task.id); }}
                    className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 flex items-center"
                    data-testid="notify-client-button"
                    title="Send notification to client"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Notify Client
                  </button>
                  {notifyDropdownTask === task.id && (
                    <div className="absolute right-0 mt-1 w-52 bg-white border rounded-lg shadow-lg z-20">
                      <button
                        onClick={() => {
                          setNotifyDropdownTask(null);
                          handleRequestInfo(task.id);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center border-b"
                        data-testid="request-info-option"
                      >
                        <MessageSquare className="w-4 h-4 mr-2 text-blue-600" />
                        Request Info
                      </button>
                      <button
                        onClick={() => {
                          setNotifyDropdownTask(null);
                          handleSendStatusUpdate(task.id);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center"
                        data-testid="status-update-option"
                      >
                        <Send className="w-4 h-4 mr-2 text-teal-600" />
                        Status Update
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Add Sub-task Button - Available to all users */}
                <button
                  onClick={() => {
                    setSelectedTaskForSubtask(task.id);
                    setShowAddSubtaskModal(true);
                  }}
                  className="bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 flex items-center"
                  data-testid="add-subtask-button"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Sub-task
                </button>
              </div>
            </div>

            {/* Reimbursements Section */}
            {task.reimbursements && task.reimbursements.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Banknote className="w-4 h-4 mr-1 text-amber-500" />
                  Reimbursements (Tax/Govt Fees):
                </p>
                <div className="space-y-2">
                  {task.reimbursements.map((reimb) => (
                    <div key={reimb.id} className="flex items-center justify-between bg-amber-50 p-2 rounded">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          reimb.type === 'Tax' ? 'bg-blue-100 text-blue-700' :
                          reimb.type === 'Govt Fee' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {reimb.type}
                        </span>
                        <span className="text-sm">{reimb.description || '-'}</span>
                        <span className="font-medium">₹{reimb.amount.toLocaleString()}</span>
                        <span className="text-xs text-gray-500">{formatDate(reimb.date)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {reimb.status === 'Collected' ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                            Collected
                          </span>
                        ) : (
                          <>
                            <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">
                              Pending
                            </span>
                            {canCreateTask && (
                              <button
                                onClick={() => handleCollectReimbursement(task.id, reimb.id)}
                                className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
                              >
                                Mark Collected
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sub-tasks */}
            {task.sub_tasks && task.sub_tasks.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Sub-tasks:</p>
                <div className="space-y-2">
                  {task.sub_tasks.map((subtask) => (
                    <div key={subtask.id} className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleSubtask(task.id, subtask.id)}
                        className="flex-shrink-0"
                      >
                        {subtask.is_completed ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      <span className={`text-sm ${subtask.is_completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                        {subtask.title}
                      </span>
                      {subtask.is_completed && subtask.completed_by && (
                        <span className="text-xs text-gray-500">
                          (by {subtask.completed_by} on {formatDateTime(subtask.completed_at)})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invoice & Receipt Info */}
            {task.invoice && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Invoice Details:</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Number:</span>
                    <span className="ml-2 font-medium">{task.invoice.number}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <span className="ml-2 font-medium">{formatDate(task.invoice.date)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Amount:</span>
                    <span className="ml-2 font-medium">₹{task.invoice.amount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {task.receipt && (
              <div className="mt-4 border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Receipt Details:</p>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Number:</span>
                    <span className="ml-2 font-medium">{task.receipt.number}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <span className="ml-2 font-medium">{formatDate(task.receipt.date)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Amount:</span>
                    <span className="ml-2 font-medium">₹{task.receipt.amount.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Mode:</span>
                    <span className="ml-2 font-medium">{task.receipt.mode}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredTasks.length === 0 && (
          <div className="text-center py-12">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {searchTerm || filterClient || filterStatus || filterPriority
                ? 'No tasks found matching your filters.'
                : 'No tasks found.'}
            </p>
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Invoice</h3>
            <form onSubmit={handleAddInvoice} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Number *</label>
                <input
                  type="text"
                  value={invoiceForm.number}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Date *</label>
                <input
                  type="date"
                  value={invoiceForm.date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={invoiceForm.description}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                  placeholder="Service description for invoice..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={invoiceForm.amount}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowInvoiceModal(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Receipt</h3>
            <form onSubmit={handleAddReceipt} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Receipt Number *</label>
                <input
                  type="text"
                  value={receiptForm.number}
                  onChange={(e) => setReceiptForm({ ...receiptForm, number: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Receipt Date *</label>
                <input
                  type="date"
                  value={receiptForm.date}
                  onChange={(e) => setReceiptForm({ ...receiptForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={receiptForm.amount}
                  onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mode *</label>
                <select
                  value={receiptForm.mode}
                  onChange={(e) => setReceiptForm({ ...receiptForm, mode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                  <option value="UPI">UPI</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowReceiptModal(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Subtask Modal */}
      {showAddSubtaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add Sub-task</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Sub-task Title *</label>
                <input
                  type="text"
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter sub-task description"
                  autoFocus
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleAddSubtask}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddSubtaskModal(false);
                    setNewSubtaskTitle('');
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Info Modal */}
      {showClientInfoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-blue-50 rounded-t-lg sticky top-0">
              <div className="flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-800">Client Information</h3>
              </div>
              <button
                onClick={() => {
                  setShowClientInfoModal(false);
                  setSelectedClientInfo(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {loadingClientInfo ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : selectedClientInfo ? (
                <div className="space-y-4">
                  {/* Client Name & Basic Info */}
                  <div className="border-b pb-4">
                    <h4 className="text-xl font-bold text-gray-900">{selectedClientInfo.name}</h4>
                    <div className="flex flex-wrap gap-4 mt-2">
                      {(selectedClientInfo.client_code || selectedClientInfo.code) && (
                        <span className="text-sm text-gray-600">
                          <span className="font-medium">Code:</span> {selectedClientInfo.client_code || selectedClientInfo.code}
                        </span>
                      )}
                      {selectedClientInfo.category && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                          {selectedClientInfo.category}
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs rounded ${selectedClientInfo.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {selectedClientInfo.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* File Number & DSC Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 flex items-center mb-1">
                        <FileText className="w-4 h-4 mr-1" /> File Number
                      </p>
                      <p className="font-medium text-gray-800">{selectedClientInfo.file_number || '-'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 mb-2">Digital Signatures (DSC)</p>
                      {selectedClientInfo.digital_signatures?.length > 0 ? (
                        <div className="space-y-2">
                          {selectedClientInfo.digital_signatures.map((dsc, idx) => (
                            <div key={idx} className="flex items-center space-x-2">
                              <span className={`w-3 h-3 rounded-full inline-block ${
                                dsc.tag_color === 'red' ? 'bg-red-500' :
                                dsc.tag_color === 'yellow' ? 'bg-yellow-500' :
                                dsc.tag_color === 'blue' ? 'bg-blue-500' :
                                dsc.tag_color === 'white' ? 'bg-gray-200 border border-gray-400' :
                                'bg-green-500'
                              }`}></span>
                              <span className="font-medium text-sm">{dsc.holder_name}</span>
                              {dsc.expiry_date && (
                                <span className="text-xs text-gray-500">
                                  (Exp: {formatDate(dsc.expiry_date)})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="font-medium text-gray-400">No DSC</p>
                      )}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-500 flex items-center">
                          <User className="w-3 h-3 mr-1" /> Contact Person
                        </p>
                        <p className="font-medium text-gray-800">{selectedClientInfo.contact_person || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 flex items-center">
                          <Phone className="w-3 h-3 mr-1" /> Phone
                        </p>
                        {selectedClientInfo.phone ? (
                          <a href={`tel:${selectedClientInfo.phone}`} className="font-medium text-blue-600 hover:underline">
                            {selectedClientInfo.phone}
                          </a>
                        ) : (
                          <p className="font-medium text-gray-400">-</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 flex items-center">
                          <Mail className="w-3 h-3 mr-1" /> Email
                        </p>
                        {selectedClientInfo.email ? (
                          <a href={`mailto:${selectedClientInfo.email}`} className="font-medium text-blue-600 hover:underline">
                            {selectedClientInfo.email}
                          </a>
                        ) : (
                          <p className="font-medium text-gray-400">-</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 flex items-center">
                          <MapPin className="w-3 h-3 mr-1" /> Address
                        </p>
                        <p className="font-medium text-gray-800">{selectedClientInfo.address || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Business Details */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Business Details</h5>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">GST Number</p>
                        <p className="font-medium text-gray-800">{selectedClientInfo.gst_number || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">PAN</p>
                        <p className="font-medium text-gray-800">{selectedClientInfo.pan || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">TAN</p>
                        <p className="font-medium text-gray-800">{selectedClientInfo.tan || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Services */}
                  {selectedClientInfo.services && selectedClientInfo.services.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-500 mb-2">Services</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedClientInfo.services.map((serviceId) => {
                          const service = services.find(s => s.id === serviceId);
                          return (
                            <span key={serviceId} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                              {service?.name || serviceId}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>Unable to load client information</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={() => {
                  setShowClientInfoModal(false);
                  setSelectedClientInfo(null);
                }}
                className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reimbursement Modal */}
      {showReimbursementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Banknote className="w-5 h-5 mr-2 text-amber-500" />
              Add Reimbursement (Tax/Govt Fee)
            </h3>
            <form onSubmit={handleAddReimbursement} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type *</label>
                <select
                  value={reimbursementForm.type}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="Govt Fee">Govt Fee</option>
                  <option value="Tax">Tax</option>
                  <option value="Stamp Duty">Stamp Duty</option>
                  <option value="Filing Fee">Filing Fee</option>
                  <option value="Challan">Challan</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={reimbursementForm.description}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., MCA Filing Fee, ROC Fee..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  value={reimbursementForm.amount}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Enter amount"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date *</label>
                <input
                  type="date"
                  value={reimbursementForm.date}
                  onChange={(e) => setReimbursementForm({ ...reimbursementForm, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600"
                >
                  Add Reimbursement
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowReimbursementModal(false);
                    setSelectedTaskForReimbursement(null);
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Non-billable Modal */}
      {showNonBillableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Ban className="w-5 h-5 mr-2 text-gray-500" />
              Mark as Non-billable
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This task will be marked as completed without requiring an invoice.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Reason for Non-billable *</label>
              <textarea
                value={nonBillableReason}
                onChange={(e) => setNonBillableReason(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                rows="3"
                placeholder="Enter reason (e.g., Free consultation, Internal task, Complimentary service...)"
                required
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleConfirmNonBillable}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
              >
                Confirm Non-billable
              </button>
              <button
                onClick={() => {
                  setShowNonBillableModal(false);
                  setSelectedTaskForNonBillable(null);
                  setNonBillableReason('');
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View/Print Receipt Modal */}
      {showViewReceiptModal && selectedTaskForViewReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50 sticky top-0">
              <h3 className="text-lg font-semibold">Payment Receipt</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePrintReceipt(selectedTaskForViewReceipt)}
                  className="flex items-center px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Receipt
                </button>
                <button
                  onClick={() => {
                    setShowViewReceiptModal(false);
                    setSelectedTaskForViewReceipt(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Receipt Preview */}
            <div className="p-6">
              <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                {/* Header */}
                <div className="text-center p-4 border-b bg-white">
                  <img src="/images/ca-logo.png" alt="CA" className="h-12 mx-auto mb-2" />
                  <h2 className="text-xl font-bold text-blue-900">HYDER ALI AND ASSOCIATES</h2>
                  <p className="text-sm font-medium text-gray-700">CHARTERED ACCOUNTANTS</p>
                  <p className="text-xs text-gray-500">XI/405,406,407, Sahara Building, Kadappadi, Peruvallur, Kondotty Malappuram, Kerala- 673638</p>
                  <p className="text-xs text-gray-500">E-Mail: cahyderali@gmail.com</p>
                </div>

                {/* Receipt Title */}
                <div className="text-center py-2 bg-teal-50 border-b font-bold text-lg text-teal-800">
                  PAYMENT RECEIPT
                </div>

                {/* Receipt Info */}
                <div className="p-4 border-b bg-white">
                  <div className="flex justify-between mb-4">
                    <div>
                      <p className="text-xs text-gray-500">Date:</p>
                      <p className="font-medium">{formatDate(new Date().toISOString())}</p>
                    </div>
                    {selectedTaskForViewReceipt.invoice && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Invoice Reference:</p>
                        <p className="font-medium">{selectedTaskForViewReceipt.invoice.number}</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500">Received From:</p>
                    <p className="font-bold text-lg">{selectedTaskForViewReceipt.client_name}</p>
                    <p className="text-sm text-gray-600">{selectedTaskForViewReceipt.service_name} - {selectedTaskForViewReceipt.task_type_name || ''}</p>
                  </div>
                </div>

                {/* Receipt Details Table */}
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-2 text-left text-sm font-medium w-16">SI No.</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Receipt No.</th>
                      <th className="px-4 py-2 text-right text-sm font-medium">Amount</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTaskForViewReceipt.receipts?.map((receipt, idx) => (
                      <tr key={receipt.id || idx} className="border-b">
                        <td className="px-4 py-3">{idx + 1}</td>
                        <td className="px-4 py-3">{formatDate(receipt.date)}</td>
                        <td className="px-4 py-3">{receipt.number}</td>
                        <td className="px-4 py-3 text-right font-medium">₹{(receipt.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-3">{receipt.mode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total */}
                <div className="flex justify-end p-4 border-b bg-green-50">
                  <div className="text-right">
                    <span className="font-bold mr-4">Total Amount Received:</span>
                    <span className="text-xl font-bold text-green-700">
                      ₹{(selectedTaskForViewReceipt.receipts?.reduce((sum, r) => sum + (r.amount || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* Thank You Message */}
                <div className="text-center py-4 bg-amber-50 border-b">
                  <p className="text-lg font-medium text-amber-700">Thank you for your payment!</p>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-4 bg-white">
                  <div className="text-right">
                    <p className="font-bold text-sm">for HYDER ALI AND ASSOCIATES</p>
                    <p className="text-xs text-gray-500 mt-8 border-t pt-1">Authorised Signatory</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
